import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { usePets } from '@/shared/hooks/queries/use-pets'
import { usePolicies } from '@/shared/hooks/queries/use-policies'
import { useCreatePetClaim } from '@/shared/hooks/queries/use-create-pet-claim'
import { useIsOffline } from '@/shared/hooks/use-is-offline'
import { useUser } from '@/shared/hooks/use-user'
import { formatApiDate } from '@/shared/helpers/format-date'
import { dataUrlToFile } from '@/shared/helpers/data-url-to-file'
import { ApiProblemError } from '@/shared/helpers/api-problem'
import { HTTP_STATUS } from '@/shared/constants/api'
import { ROUTES } from '@/shared/constants/routes'
import {
  ACTIVE_POLICY_STATUS,
  CLAIM_PROBLEM_TYPE,
  PET_CLAIM_PLEDGE_FILE_NAME,
} from '@/shared/constants/claims'
import { useClaimFormSteps } from '@/shared/hooks/use-claim-form-steps'
import { useSubmitClaimModal } from '@/shared/hooks/use-submit-claim-modal'
import { buildPetClaimCard } from '@/shared/helpers/pet-claim-card'

const FORM_STEP_COUNT = 2

const STEP_ONE_FIELDS = [
  'petId',
  'invoiceDate',
  'amount',
  'description',
] as const

export type PetClaimFormValues = {
  petId: string
  invoiceDate: Date | undefined
  description: string
  /** Cents, as a digit string (the input is cash-register style) */
  amount: string
  receipts: File[]
  petPicture: File | undefined
  signature: string | undefined
}

// Claims API form part → form field
const API_FIELD_TO_FORM_FIELD: Record<string, keyof PetClaimFormValues> = {
  amount: 'amount',
  invoice_date: 'invoiceDate',
  description: 'description',
  invoices: 'receipts',
  pledge_of_honor: 'signature',
  pet_picture: 'petPicture',
}

export function useClaimsPetPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isOffline = useIsOffline()
  const { user } = useUser()
  const { currentStep, isFlipping, isLastStep, flipToStep, handleBack } =
    useClaimFormSteps(FORM_STEP_COUNT)
  const [submitErrorKey, setSubmitErrorKey] = useState<string | undefined>(
    undefined,
  )

  const { data: pets = [], isLoading: isPetsLoading } = usePets()
  const { data: policies = [], isLoading: isPoliciesLoading } = usePolicies()
  const createClaimMutation = useCreatePetClaim()
  // Once saved, the claim is a draft: open it if not submitted now, or go to
  // the list once it has been
  const submitModal = useSubmitClaimModal({
    onCancel: (claimId) =>
      navigate({ to: ROUTES.CLAIM_DETAIL, params: { claimId } }),
    onFinish: () => navigate({ to: ROUTES.CLAIMS }),
  })

  const formMethods = useForm<PetClaimFormValues>({
    defaultValues: {
      petId: '',
      invoiceDate: undefined,
      description: '',
      amount: '',
      receipts: [],
      petPicture: undefined,
      signature: undefined,
    },
    mode: 'all',
  })

  const {
    watch,
    trigger,
    setError,
    handleSubmit: formHandleSubmit,
  } = formMethods

  const selectedPetId = watch('petId')
  const selectedPet = useMemo(
    () => pets.find((pet) => String(pet.id) === selectedPetId),
    [pets, selectedPetId],
  )
  const selectedPolicy = useMemo(
    () =>
      policies.find(
        (policy) =>
          policy.status === ACTIVE_POLICY_STATUS &&
          String(policy.petId) === selectedPetId,
      ),
    [policies, selectedPetId],
  )

  const handleSubmitError = useCallback(
    (error: Error) => {
      if (!(error instanceof ApiProblemError)) {
        setSubmitErrorKey('claims.pet.errors.generic')
        return
      }
      switch (error.status) {
        case HTTP_STATUS.UNPROCESSABLE: {
          const fieldErrors = Object.entries(error.fieldErrors)
          fieldErrors.forEach(([apiField, detail]) => {
            const formField = API_FIELD_TO_FORM_FIELD[apiField]
            if (formField) setError(formField, { message: detail })
          })
          const hasStepOneError = fieldErrors.some(([apiField]) =>
            (STEP_ONE_FIELDS as readonly string[]).includes(
              API_FIELD_TO_FORM_FIELD[apiField] ?? '',
            ),
          )
          if (hasStepOneError) flipToStep(0)
          setSubmitErrorKey('claims.pet.errors.validation')
          return
        }
        case HTTP_STATUS.CONFLICT:
          setSubmitErrorKey(
            error.problem.type === CLAIM_PROBLEM_TYPE.POLICY_NOT_CLAIMABLE
              ? 'claims.pet.errors.policyNotClaimable'
              : 'claims.pet.errors.generic',
          )
          return
        case HTTP_STATUS.NOT_FOUND:
          setSubmitErrorKey('claims.pet.errors.policyNotFound')
          return
        case HTTP_STATUS.PAYLOAD_TOO_LARGE:
          setSubmitErrorKey('claims.pet.errors.filesTooLarge')
          return
        case HTTP_STATUS.UNSUPPORTED_MEDIA_TYPE:
          setSubmitErrorKey('claims.pet.errors.unsupportedFile')
          return
        default:
          setSubmitErrorKey('claims.pet.errors.generic')
      }
    },
    [setError, flipToStep],
  )

  const submitClaim = formHandleSubmit((values) => {
    const policyId = Number(selectedPolicy?.policyExternalId)
    if (!policyId || !values.invoiceDate || !values.signature) return
    setSubmitErrorKey(undefined)
    createClaimMutation.mutate(
      {
        policyId,
        amount: Number(values.amount),
        invoiceDate: formatApiDate(values.invoiceDate),
        description: values.description.trim(),
        invoices: values.receipts,
        pledgeOfHonor: dataUrlToFile(
          values.signature,
          PET_CLAIM_PLEDGE_FILE_NAME,
        ),
        petPicture: values.petPicture,
      },
      {
        // No claim back means it was accepted but is still being processed:
        // it shows up in the list once ready
        onSuccess: (claim) =>
          claim ? submitModal.open(claim) : navigate({ to: ROUTES.CLAIMS }),
        onError: handleSubmitError,
      },
    )
  })

  const handleNext = useCallback(async () => {
    if (currentStep < FORM_STEP_COUNT - 1) {
      const isStepValid = await trigger([...STEP_ONE_FIELDS])
      if (isStepValid) flipToStep(currentStep + 1)
      return
    }
    submitClaim()
  }, [currentStep, trigger, flipToStep, submitClaim])

  function handleCancel() {
    navigate({ to: ROUTES.HOME })
  }

  return {
    formMethods,
    currentStep,
    isFlipping,
    isLastStep,
    hasSelectedPet: !!selectedPet,
    card: buildPetClaimCard(
      t,
      selectedPet,
      selectedPolicy,
      user?.fullName ?? '',
    ),
    isDataLoading: isPetsLoading || isPoliciesLoading,
    isSubmitting: createClaimMutation.isPending,
    submitErrorKey,
    isOffline,
    submitModal,
    handleNext,
    handleBack,
    handleCancel,
  }
}
